import { create } from "zustand";

/**
 * Single, minimal Zustand store for limbo (hard rule: one store per app).
 *
 *   idle → rolling (suspense count-up) → done
 *
 * The roll itself is server-side and instant; "rolling" only paces the reveal.
 */

export type Phase = "idle" | "rolling" | "done";

export interface RollResult {
  won: boolean;
  roll: number;
  target: number;
  value?: number;
  rank?: number;
  total?: number;
  percentile?: number;
}

export interface PastRoll {
  roll: number;
  target: number;
  won: boolean;
}

interface LimboState {
  phase: Phase;
  /** The called multiplier (persisted — people have a number). */
  target: number;
  result: RollResult | null;
  history: PastRoll[];
  /** Best landed call ever (×100), localStorage-backed. */
  best: number;

  setTarget: (t: number) => void;
  startRoll: () => void;
  land: (result: RollResult) => void;
  reset: () => void;
}

const TARGET_KEY = "kp:limbo:target";
const BEST_KEY = "kp:limbo:best";
const HISTORY_KEY = "kp:limbo:history";
const HISTORY_LEN = 8;

function loadTarget(): number {
  try {
    const v = Number.parseFloat(localStorage.getItem(TARGET_KEY) ?? "2");
    return Number.isFinite(v) && v >= 1.01 && v <= 1000 ? v : 2;
  } catch {
    return 2;
  }
}
function loadBest(): number {
  try {
    return Number.parseInt(localStorage.getItem(BEST_KEY) ?? "0", 10) || 0;
  } catch {
    return 0;
  }
}
function loadHistory(): PastRoll[] {
  try {
    const arr = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]") as unknown;
    return Array.isArray(arr) ? (arr as PastRoll[]).slice(0, HISTORY_LEN) : [];
  } catch {
    return [];
  }
}
function save(key: string, v: string): void {
  try {
    localStorage.setItem(key, v);
  } catch {
    /* blocked — fine */
  }
}

export const useLimboStore = create<LimboState>((set, get) => ({
  phase: "idle",
  target: loadTarget(),
  result: null,
  history: loadHistory(),
  best: loadBest(),

  setTarget: (t) => {
    save(TARGET_KEY, String(t));
    set({ target: t });
  },

  startRoll: () => set({ phase: "rolling", result: null }),

  land: (result) => {
    const history = [
      { roll: result.roll, target: result.target, won: result.won },
      ...get().history,
    ].slice(0, HISTORY_LEN);
    save(HISTORY_KEY, JSON.stringify(history));
    let best = get().best;
    if (result.won && result.value !== undefined && result.value > best) {
      best = result.value;
      save(BEST_KEY, String(best));
    }
    set({ phase: "done", result, history, best });
  },

  reset: () => set({ phase: "idle", result: null }),
}));
